import {
  Alert,
  AssignedCallPrompt,
  Button,
  DetailField,
  sentenceCase,
} from '@relay-towing/ui';
import { Route } from 'lucide-react';
import * as React from 'react';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';
import { CallsMap, hasPoint } from '../map/CallsMap';
import { dayAndTime } from '../status';
import type { CallRecord } from './callRecord';
import { PlaceSheet } from './PlaceSheet';

/**
 * "You've been assigned a call" for one call, as the Figma's Modal fills
 * it: how far, a small map of the pickup, then Type, Priority, Pickup
 * Location with its maps panel (Google Maps, Apple Maps or copy, as on the
 * call), Customer and Dispatched. The shell's
 * AssignedCallWatcher decides which call and when. The map is left out
 * when maps are not working or the call has no pickup to pin.
 *
 * Dispatched is when the call was scheduled for, or else taken, as the
 * assignment's text message says it (call.service.ts); the call has no
 * start time until the driver starts it.
 */
export function AssignedCallSheet({
  call,
  onOkay,
  onStart,
  starting,
  error,
}: {
  call: CallRecord;
  onOkay: () => void;
  onStart: () => void;
  starting: boolean;
  error: string | null;
}) {
  const { mapsAvailable } = useGoogleMaps();
  const [placing, setPlacing] = React.useState(false);
  const dispatched =
    typeof call.scheduledAt === 'string' ? call.scheduledAt : call.createdAt;

  return (
    <AssignedCallPrompt
      open
      onOpenChange={(open) => {
        if (!open) onOkay();
      }}
      distance={
        call.routeDistanceToPickup !== null &&
        call.routeDistanceToPickup !== undefined
          ? `${call.routeDistanceToPickup.toFixed(1)} miles`
          : undefined
      }
      map={
        mapsAvailable && hasPoint(call) ? (
          <CallsMap calls={[call]} />
        ) : undefined
      }
      onOkay={onOkay}
      onStart={onStart}
      starting={starting}
    >
      {error ? <Alert tone="destructive">{error}</Alert> : null}
      {call.towType ? (
        <DetailField label="Type" value={sentenceCase(call.towType)} />
      ) : null}
      <DetailField label="Priority" value={sentenceCase(call.priority)} />
      <DetailField
        label="Pickup Location"
        value={call.pickupLocation}
        action={
          <Button
            variant="secondary"
            size="icon-lg"
            pill
            aria-label="Open in maps"
            onClick={() => setPlacing(true)}
          >
            <Route />
          </Button>
        }
      />
      <PlaceSheet
        title="Pickup location"
        place={{
          address: call.pickupLocation,
          lat: call.pickupLocationLat,
          lng: call.pickupLocationLong,
        }}
        open={placing}
        onOpenChange={setPlacing}
      />
      <DetailField
        label="Customer"
        value={call.contactName || call.contactPhone}
      />
      {dispatched ? (
        <DetailField label="Dispatched" value={dayAndTime(dispatched)} />
      ) : null}
    </AssignedCallPrompt>
  );
}
